import {
  Grid,
  Button,
  Typography,
  Box,
  Alert,
  Autocomplete,
  TextField,
  CircularProgress,
  FormControl,
  Select,
  MenuItem
} from '@mui/material';
import { styled } from '@mui/material/styles';
import React, {
  useState,
  useMemo,
  useEffect,
  useContext,
  forwardRef,
  useImperativeHandle
} from 'react';
import { useQuery } from '@tanstack/react-query';
import axios from 'src/axios';
import { APICONSTANTS, CONSTANTS } from 'src/components/Constants';
import { SnackContext, AppContext } from 'src/store/ContextStore';
import TextInput from 'src/components/UI/input/TextInput';
import FormLabel from 'src/components/UI/input/CustomFormLabel';

const ButtonGroup = styled(Grid)({
  display: 'flex',
  justifyContent: 'center',
  marginTop: 15
});

const PGS_ID_SHAPE = /^pgs\d+$/i;

// Debounce a value so search doesn't hammer the PGS Catalog proxy on every
// keystroke — same pattern as DiseaseAutocomplete.jsx.
function useDebounced(value, delayMs) {
  const [debounced, setDebounced] = useState(value);
  React.useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(t);
  }, [value, delayMs]);
  return debounced;
}

// One search box, two query shapes handled server-side: `term` is tried both
// as an exact PGS id and as a trait/disease keyword, and every result comes
// back with full score metadata (name, variant count, publication) — not
// just an id — so the picker can show enough detail to tell scores apart
// before committing to one.
async function searchPgsScores(term) {
  const { data } = await axios.get(APICONSTANTS.searchPgsScores, {
    params: { q: term }
  });
  return data?.results || [];
}

const pgsOptionLabel = (o) =>
  typeof o === 'string'
    ? o
    : `${o.pgsId} — ${o.pgsName || o.traitReported || o.pgsId}`;

// PGS Catalog's ancestry_distribution codes → readable labels. Verified
// against a real API response (dev/eval/gwas → dist of these codes) plus
// pgscatalog.org/docs/ancestry/ for the full category names; anything not
// in this list falls back to showing the raw code rather than a guess.
const ANCESTRY_CODE_LABELS = {
  EUR: 'European',
  AFR: 'African',
  AAC: 'African American / Afro-Caribbean',
  EAS: 'East Asian',
  SAS: 'South Asian',
  SEA: 'South East Asian',
  CAS: 'Central Asian',
  ASN: 'Asian (unspecified)',
  AMR: 'Hispanic or Latin American',
  GME: 'Greater Middle Eastern',
  OCE: 'Oceanian',
  NR: 'Not reported',
  MAE: 'Multi-ancestry (incl. European)',
  MAO: 'Multi-ancestry (excl. European)'
};

// Maps this form's own ancestry picker (CONSTANTS.prsAncestryOptions) to the
// PGS Catalog code(s) representing the same group, so a patient's selected
// ancestry can be checked against a score's own distribution.
const PATIENT_ANCESTRY_TO_PGS_CODES = {
  european: ['EUR'],
  'east-asian': ['EAS'],
  'south-asian': ['SAS'],
  african: ['AFR'],
  'african-american': ['AAC', 'AFR'],
  'hispanic-latin-american': ['AMR'],
  'greater-middle-eastern': ['GME'],
  oceanian: ['OCE'],
  'multiple-mixed': ['MAE', 'MAO']
};

// eval (the score's validation cohort) says the most about real-world
// accuracy in a population; dev/gwas are fallbacks when eval isn't reported.
function pickAncestryBucket(distribution) {
  if (!distribution) return null;
  if (distribution.eval)
    return { dist: distribution.eval.dist, label: 'validation' };
  if (distribution.dev)
    return { dist: distribution.dev.dist, label: 'training' };
  if (distribution.gwas)
    return { dist: distribution.gwas.dist, label: 'GWAS source' };
  return null;
}

/**
 * Polygenic Risk Score (PRS) risk-factor INPUT form. Self-contained: manages
 * its own field state, loads any existing prs record, and saves via
 * `savePrsRecord`. Used by the Clinical Records "Risk Factor" modal, as a
 * peer to GailForm / IndividualDataForm (CanRisk).
 *
 * TrakGene does not store raw genotype/VCF data, so a score is never
 * computed here — it's either typed in from a lab report or entered
 * manually. The "Look up on PGS Catalog" action is a convenience that
 * auto-fills trait/publication context for a PGS id the clinician already
 * has; a failed lookup never blocks saving.
 *
 * Props mirror GailForm:
 * - data     — selected pedigree node (dob, pid, user_id).
 * - onSaved  — called after the record is saved.
 * - disabled — read-only mode.
 * - hideActionButton — hide the in-form Save button; the parent triggers the
 *   save via the imperative `submit()` handle (used by the modal footer).
 */
const PrsForm = forwardRef(function PrsForm(
  { data, model, onSaved, disabled = false, hideActionButton = false },
  ref
) {
  const { setSnack } = useContext(SnackContext);
  const { content } = useContext(AppContext);
  const { pedigreeSelectedUsers: users } = content;
  const { selectedUser: user } = users;

  const [loading, setLoading] = useState(false);
  const [lookingUp, setLookingUp] = useState(false);
  const [lookupError, setLookupError] = useState(null);
  const [lookupMeta, setLookupMeta] = useState(null); // pgsName/publication/variantsNumber, display-only
  const [pgsSearchInput, setPgsSearchInput] = useState('');
  const [selectedPgsOption, setSelectedPgsOption] = useState(null); // { pgsId, trait }
  const debouncedPgsSearch = useDebounced(pgsSearchInput, 250);
  const [formValues, setFormValues] = useState({
    pgsId: '',
    trait: '',
    traitId: '',
    ancestry: 'unknown',
    zscore: '',
    percentile: '',
    relativeRisk: '',
    source: 'manual',
    notes: '',
    rawScore: '',
    variantCoveragePct: ''
  });

  // "Compute from VCF" proof-of-concept panel — separate from the rest of
  // the form's state since it's an optional, self-contained mini-flow.
  const [computeOpen, setComputeOpen] = useState(false);
  const [computeGenomeBuild, setComputeGenomeBuild] = useState('GRCh38');
  const [computeFile, setComputeFile] = useState(null);
  const [computing, setComputing] = useState(false);
  const [computeResult, setComputeResult] = useState(null);
  const [computeError, setComputeError] = useState(null);

  const user_id = useMemo(() => user?.user_id || user?._id, [user]);
  const familyMemberId = useMemo(() => data?.familyMemberId, [data]);
  const proBandId = useMemo(() => data?.proBandId, [data]);
  // PRS records are stored against this id; prefill must read by the same id.
  const memberId = useMemo(
    () => proBandId ?? familyMemberId,
    [proBandId, familyMemberId]
  );

  const applyRecord = (rec) => {
    if (!rec?.prsResult) return;
    const {
      pgsId,
      pgsName,
      trait,
      traitId,
      ancestry,
      zscore,
      percentile,
      relativeRisk,
      source,
      publication,
      variantsNumber,
      ancestryDistribution,
      rawScore,
      variantCoveragePct,
      notes
    } = rec.prsResult;

    setFormValues({
      pgsId: pgsId || '',
      trait: trait || '',
      traitId: traitId || '',
      ancestry: ancestry || 'unknown',
      zscore: zscore != null ? String(zscore) : '',
      percentile: percentile != null ? String(percentile) : '',
      relativeRisk: relativeRisk != null ? String(relativeRisk) : '',
      source: source || 'manual',
      rawScore: rawScore != null ? String(rawScore) : '',
      variantCoveragePct:
        variantCoveragePct != null ? String(variantCoveragePct) : '',
      notes: notes || ''
    });
    if (
      pgsName ||
      publication ||
      variantsNumber != null ||
      ancestryDistribution
    ) {
      setLookupMeta({
        pgsName,
        publication,
        variantsNumber,
        ancestryDistribution
      });
    }
    if (pgsId) {
      setPgsSearchInput(pgsId);
      setSelectedPgsOption({
        pgsId,
        pgsName: pgsName || null,
        traitReported: trait || null,
        variantsNumber: variantsNumber ?? null,
        publication: publication || null
      });
    }
  };

  useEffect(() => {
    // Editing a specific record prefills from it; a new record starts blank
    // (unlike Gail, a PRS score is per-condition, not a single running set of
    // inputs, so there's no "latest inputs" convenience prefill).
    if (model?._id) {
      applyRecord(model);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [model?._id]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormValues((prev) => ({ ...prev, [name]: value }));
  };

  // Manual fallback: fetches metadata for whatever raw id is currently typed
  // (the "Look up" button, or a freeSolo Enter-commit with no option picked)
  // — search results already carry full metadata, so selecting one doesn't
  // need this round trip.
  const handleLookup = (idOverride) => {
    const pgsId = (idOverride ?? formValues.pgsId)?.trim();
    if (!pgsId) return;

    setLookingUp(true);
    setLookupError(null);
    axios
      .get(APICONSTANTS.getPgsScore.replace(':pgsId', pgsId))
      .then(({ data: score }) => {
        setFormValues((prev) => ({
          ...prev,
          pgsId: score.pgsId || pgsId,
          trait: score.traitReported || prev.trait,
          traitId: score.traitEfo?.[0]?.id || prev.traitId
        }));
        setLookupMeta({
          pgsName: score.pgsName,
          publication: score.publication,
          variantsNumber: score.variantsNumber,
          ancestryDistribution: score.ancestryDistribution
        });
      })
      .catch((err) => {
        setLookupMeta(null);
        setLookupError(
          err?.response?.status === 404
            ? `${pgsId} was not found on the PGS Catalog.`
            : 'PGS Catalog lookup failed — you can still enter the score manually.'
        );
      })
      .finally(() => setLookingUp(false));
  };

  // Resets everything tied to a chosen score — used when the search field is
  // cleared, so the metadata box below it doesn't keep showing a score that
  // no longer matches what's in the field.
  const clearPgsSelection = () => {
    setSelectedPgsOption(null);
    setLookupMeta(null);
    setLookupError(null);
    setFormValues((prev) => ({ ...prev, pgsId: '' }));
  };

  // Proof-of-concept: compute a raw PRS from an uploaded VCF, scoped to a
  // single hardcoded score (PGS000004 / PRS313_BC — the backend rejects
  // anything else). The uploaded file is sent once and never touched again
  // on this end either — no local copy is kept past this call.
  const handleComputeFromVcf = () => {
    if (!computeFile) return;
    setComputing(true);
    setComputeError(null);
    setComputeResult(null);

    const body = new FormData();
    body.append('vcf', computeFile);
    body.append('pgsId', 'PGS000004');
    body.append('genomeBuild', computeGenomeBuild);

    axios
      .post(APICONSTANTS.computePrsFromVcf, body, {
        headers: { 'Content-Type': 'multipart/form-data' }
      })
      .then(({ data }) => {
        setComputeResult(data);
        setFormValues((prev) => ({
          ...prev,
          pgsId: data.pgsId,
          source: 'trakgene-poc',
          rawScore: String(data.rawScore),
          variantCoveragePct: String(data.coveragePct),
          // Only present when a verified CanRisk reference exists for this
          // exact score (PGS000004 today) — a real sourced z-score, not
          // fabricated, so it's safe to prefill here unlike the raw score.
          ...(data.zscore != null ? { zscore: String(data.zscore) } : {})
        }));
        setPgsSearchInput(data.pgsId);
        setSelectedPgsOption({ pgsId: data.pgsId, pgsName: data.pgsName });
        // Reuse the existing lookup path to fill in trait/publication —
        // the compute result only carries the score's name, not its trait.
        handleLookup(data.pgsId);
      })
      .catch((err) => {
        setComputeError(
          err?.response?.status === 400
            ? 'This proof of concept only supports PRS313_BC (PGS000004) — check the file is a valid single-sample VCF.'
            : 'Compute failed — double-check the genome build matches the file, then try again.'
        );
      })
      .finally(() => setComputing(false));
  };

  const { data: pgsOptions = [], isFetching: isSearchingPgs } = useQuery({
    queryKey: ['pgs-score-search', debouncedPgsSearch],
    queryFn: () => searchPgsScores(debouncedPgsSearch),
    enabled: debouncedPgsSearch.trim().length >= 2,
    staleTime: 60_000
  });

  const savePrsHandle = () => {
    if (!memberId || !user_id) {
      return setSnack({
        status: true,
        msg: 'Please save the pedigree first',
        severity: 'error'
      });
    }
    if (!formValues.pgsId && !formValues.trait) {
      return setSnack({
        status: true,
        msg: 'Enter a PGS id or a trait before saving',
        severity: 'error'
      });
    }

    setLoading(true);
    axios
      .post(APICONSTANTS.savePrsRecord, {
        recordId: model?.recordType === 'prs' ? model?._id : undefined,
        user_id,
        familyId: user.familyId,
        familyMemberId: memberId,
        prsResult: {
          pgsId: formValues.pgsId || undefined,
          pgsName: lookupMeta?.pgsName,
          trait: formValues.trait || undefined,
          traitId: formValues.traitId || undefined,
          ancestry: formValues.ancestry,
          zscore: formValues.zscore !== '' ? Number(formValues.zscore) : null,
          percentile:
            formValues.percentile !== '' ? Number(formValues.percentile) : null,
          relativeRisk:
            formValues.relativeRisk !== ''
              ? Number(formValues.relativeRisk)
              : null,
          variantsNumber: lookupMeta?.variantsNumber,
          source: formValues.source,
          publication: lookupMeta?.publication,
          ancestryDistribution: lookupMeta?.ancestryDistribution,
          rawScore:
            formValues.rawScore !== '' ? Number(formValues.rawScore) : null,
          variantCoveragePct:
            formValues.variantCoveragePct !== ''
              ? Number(formValues.variantCoveragePct)
              : null,
          notes: formValues.notes || undefined
        }
      })
      .then((resp) => {
        setSnack({
          status: true,
          msg: 'PRS record saved',
          severity: 'success'
        });
        onSaved?.(resp.data);
      })
      .catch(() => {
        setSnack({
          status: true,
          msg: 'An error has occurred!',
          severity: 'error'
        });
      })
      .finally(() => setLoading(false));
  };

  // Let a parent (e.g. the Risk Factor modal footer) trigger the save.
  useImperativeHandle(ref, () => ({
    submit: () => savePrsHandle()
  }));

  const isSaveDisabled = disabled || loading;

  const ancestryBucket = useMemo(
    () => pickAncestryBucket(lookupMeta?.ancestryDistribution),
    [lookupMeta?.ancestryDistribution]
  );

  // % of the score's own population that matches the patient's selected
  // ancestry group — null when the patient's ancestry is Unknown/unmapped,
  // since there's nothing to compare against.
  const patientAncestryPct = useMemo(() => {
    if (!ancestryBucket?.dist) return null;
    const codes = PATIENT_ANCESTRY_TO_PGS_CODES[formValues.ancestry];
    if (!codes) return null;
    return codes.reduce(
      (sum, code) => sum + (ancestryBucket.dist[code] || 0),
      0
    );
  }, [ancestryBucket, formValues.ancestry]);

  return (
    <Grid container spacing={2}>
      <Grid size={12}>
        <Alert severity="info" sx={{ mb: 1 }}>
          No genotype/VCF data is stored, so a score can&apos;t be calculated
          here — enter a score you already have from a lab report or manual
          record. Look up a PGS id to auto-fill trait and publication context.
        </Alert>
      </Grid>
      <Grid size={12}>
        <FormLabel>PGS Catalog score</FormLabel>
        <Autocomplete
          size="small"
          freeSolo
          options={pgsOptions}
          getOptionLabel={pgsOptionLabel}
          isOptionEqualToValue={(a, b) => a?.pgsId === b?.pgsId}
          value={selectedPgsOption}
          onChange={(_, sel) => {
            if (!sel) {
              clearPgsSelection();
              return;
            }
            if (typeof sel === 'string') {
              setSelectedPgsOption({ pgsId: sel });
              setFormValues((prev) => ({ ...prev, pgsId: sel }));
              handleLookup(sel);
              return;
            }
            setSelectedPgsOption(sel);
            setFormValues((prev) => ({
              ...prev,
              pgsId: sel.pgsId,
              trait: sel.traitReported || prev.trait,
              traitId: sel.traitEfo?.[0]?.id || prev.traitId
            }));
            setLookupMeta({
              pgsName: sel.pgsName,
              publication: sel.publication,
              variantsNumber: sel.variantsNumber,
              ancestryDistribution: sel.ancestryDistribution
            });
          }}
          inputValue={pgsSearchInput}
          onInputChange={(_, v, reason) => {
            setPgsSearchInput(v);
            if ((reason === 'input' || reason === 'clear') && !v.trim()) {
              clearPgsSelection();
              return;
            }
            if (reason === 'input' && PGS_ID_SHAPE.test(v.trim())) {
              setFormValues((prev) => ({ ...prev, pgsId: v.trim() }));
            }
          }}
          loading={isSearchingPgs}
          disabled={disabled}
          noOptionsText={
            pgsSearchInput.trim().length < 2
              ? 'Type a PGS id (e.g. PGS000004) or a disease name…'
              : 'No matching PGS ids or traits'
          }
          renderOption={(props, option) => (
            <li {...props} key={option.pgsId}>
              <Box sx={{ py: 0.25 }}>
                <Typography variant="body2">
                  <strong>{option.pgsName || option.pgsId}</strong>
                  {option.pgsName && ` (${option.pgsId})`}
                  {option.variantsNumber != null &&
                    ` — ${option.variantsNumber} variants`}
                </Typography>
                {(option.publication?.title || option.traitReported) && (
                  <Typography
                    variant="caption"
                    sx={{ color: 'text.secondary', display: 'block' }}
                  >
                    {option.publication?.title || option.traitReported}
                    {option.publication?.journal &&
                      ` (${option.publication.journal})`}
                    {option.publication?.pmid &&
                      ` — PMID ${option.publication.pmid}`}
                  </Typography>
                )}
              </Box>
            </li>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder="e.g. PGS000004 or breast cancer"
              slotProps={{
                ...params.slotProps,
                input: {
                  ...params.slotProps.input,
                  endAdornment: (
                    <>
                      {isSearchingPgs ? <CircularProgress size={16} /> : null}
                      {params.slotProps.input.endAdornment}
                    </>
                  )
                }
              }}
            />
          )}
        />
      </Grid>
      {/* <Grid size={12}>
        <Button
          variant="text"
          size="small"
          onClick={() => setComputeOpen((v) => !v)}
          disabled={disabled}
          sx={{ textTransform: 'none' }}
        >
          {computeOpen ? 'Hide' : 'Show'} &quot;Compute from VCF&quot; (proof of
          concept — PRS313_BC only)
        </Button>
        {computeOpen && (
          <Box
            sx={{
              border: '1px dashed',
              borderColor: 'divider',
              borderRadius: '8px',
              p: 1.5,
              mt: 1
            }}
          >
            <Alert severity="warning" sx={{ mb: 1.5 }}>
              Experimental. Computes an unnormalised raw score from an uploaded
              VCF. A calibrated z-score is also shown, sourced from
              CanRisk&apos;s own published reference for this specific score —
              not TrakGene&apos;s own normalisation, and not independently
              validated. The file is processed for this request only and is
              never saved anywhere.
            </Alert>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <FormLabel>Genome build</FormLabel>
                <FormControl fullWidth size="small">
                  <Select
                    value={computeGenomeBuild}
                    onChange={(e) => setComputeGenomeBuild(e.target.value)}
                    disabled={disabled || computing}
                  >
                    <MenuItem value="GRCh38">GRCh38 (hg38)</MenuItem>
                    <MenuItem value="GRCh37">GRCh37 (hg19)</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <FormLabel>VCF file (plain text, not gzipped)</FormLabel>
                <Button
                  variant="outlined"
                  component="label"
                  size="small"
                  fullWidth
                  disabled={disabled || computing}
                  sx={{
                    height: 40,
                    justifyContent: 'flex-start',
                    textTransform: 'none'
                  }}
                >
                  {computeFile ? computeFile.name : 'Choose file…'}
                  <input
                    type="file"
                    accept=".vcf,text/plain"
                    hidden
                    onChange={(e) =>
                      setComputeFile(e.target.files?.[0] || null)
                    }
                  />
                </Button>
              </Grid>
              <Grid size={12}>
                <Button
                  variant="contained"
                  color="secondary"
                  size="small"
                  onClick={handleComputeFromVcf}
                  loading={computing}
                  disabled={disabled || computing || !computeFile}
                >
                  Compute
                </Button>
              </Grid>
              {computeError && (
                <Grid size={12}>
                  <Alert severity="error">{computeError}</Alert>
                </Grid>
              )}
              {computeResult && (
                <Grid size={12}>
                  <Typography variant="body2">
                    Raw score: <strong>{computeResult.rawScore}</strong> —{' '}
                    {computeResult.variantsMatched}/
                    {computeResult.variantsTotal} variants matched (
                    {computeResult.coveragePct}%)
                  </Typography>
                  {computeResult.zscore != null && (
                    <Typography variant="body2">
                      Z-score:{' '}
                      <strong>{computeResult.zscore.toFixed(4)}</strong> —
                      calibrated using CanRisk&apos;s published reference for
                      &quot;{computeResult.canriskPanelName}&quot; (alpha=
                      {computeResult.canriskAlpha})
                    </Typography>
                  )}
                  {computeResult.warnings?.map((w) => (
                    <Typography
                      key={w}
                      variant="caption"
                      sx={{ color: 'warning.main', display: 'block' }}
                    >
                      {w}
                    </Typography>
                  ))}
                  <Typography
                    variant="caption"
                    sx={{ color: 'text.secondary', display: 'block', mt: 0.5 }}
                  >
                    {computeResult.disclaimer}
                  </Typography>
                </Grid>
              )}
            </Grid>
          </Box>
        )}
      </Grid> */}
      {lookupError && (
        <Grid size={12}>
          <Alert severity="warning">{lookupError}</Alert>
        </Grid>
      )}
      {lookupMeta && (
        <Grid size={12}>
          <Box
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: '8px',
              p: 1.5
            }}
          >
            {lookupMeta.pgsName && (
              <Typography variant="body2">
                <strong>{lookupMeta.pgsName}</strong>
                {lookupMeta.variantsNumber != null &&
                  ` — ${lookupMeta.variantsNumber} variants`}
              </Typography>
            )}
            {lookupMeta.publication?.title && (
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                {lookupMeta.publication.title}
                {lookupMeta.publication.journal &&
                  ` (${lookupMeta.publication.journal})`}
                {lookupMeta.publication.pmid &&
                  ` — PMID ${lookupMeta.publication.pmid}`}
              </Typography>
            )}
          </Box>
        </Grid>
      )}
      <Grid size={{ xs: 12, sm: 6 }}>
        <TextInput
          label="Trait"
          name="trait"
          value={formValues.trait}
          onChange={handleChange}
          placeholder="e.g. Breast cancer"
          disabled={disabled}
        />
      </Grid>
      <Grid size={{ xs: 12, sm: 6 }}>
        <Box sx={{ mb: 1 }}>
          <FormLabel>Ancestry</FormLabel>
        </Box>
        <FormControl fullWidth size="large">
          <Select
            name="ancestry"
            value={formValues.ancestry}
            onChange={handleChange}
            disabled={disabled}
          >
            {CONSTANTS.prsAncestryOptions.map((o) => (
              <MenuItem key={o.value} value={o.value}>
                {o.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
      {ancestryBucket?.dist && (
        <Grid size={12}>
          <Box
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: '8px',
              p: 1.5
            }}
          >
            <Typography
              variant="caption"
              sx={{ color: 'text.secondary', display: 'block', mb: 0.5 }}
            >
              This score&apos;s {ancestryBucket.label} population:
            </Typography>
            <Typography variant="body2">
              {Object.entries(ancestryBucket.dist)
                .sort((a, b) => b[1] - a[1])
                .map(
                  ([code, pct]) =>
                    `${ANCESTRY_CODE_LABELS[code] || code} ${pct}%`
                )
                .join(', ')}
            </Typography>
            {patientAncestryPct != null &&
              (patientAncestryPct < 15 ? (
                <Alert severity="warning" sx={{ mt: 1 }}>
                  The patient&apos;s ancestry makes up only {patientAncestryPct}
                  % of this score&apos;s {ancestryBucket.label} population —
                  interpret this result with extra caution.
                </Alert>
              ) : (
                <Typography
                  variant="caption"
                  sx={{ color: 'success.main', display: 'block', mt: 0.5 }}
                >
                  Matches {patientAncestryPct}% of this score&apos;s{' '}
                  {ancestryBucket.label} population.
                </Typography>
              ))}
          </Box>
        </Grid>
      )}
      <Grid size={{ xs: 12, sm: 4 }}>
        <TextInput
          label="Z-score"
          type="number"
          name="zscore"
          value={formValues.zscore}
          onChange={handleChange}
          disabled={disabled}
        />
      </Grid>
      <Grid size={{ xs: 12, sm: 4 }}>
        <TextInput
          label="Percentile"
          type="number"
          name="percentile"
          value={formValues.percentile}
          onChange={handleChange}
          disabled={disabled}
        />
      </Grid>
      <Grid size={{ xs: 12, sm: 4 }}>
        <TextInput
          label="Relative risk / odds ratio"
          type="number"
          name="relativeRisk"
          value={formValues.relativeRisk}
          onChange={handleChange}
          disabled={disabled}
        />
      </Grid>
      <Grid size={12}>
        <Box sx={{ mb: 1 }}>
          <FormLabel>Score source</FormLabel>
        </Box>
        <FormControl fullWidth size="large">
          <Select
            name="source"
            value={formValues.source}
            onChange={handleChange}
            disabled={disabled}
          >
            {CONSTANTS.prsSourceOptions.map((o) => (
              <MenuItem key={o.value} value={o.value}>
                {o.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
      <Grid size={12}>
        <FormLabel>Notes</FormLabel>
        <TextInput
          type="textarea"
          name="notes"
          value={formValues.notes}
          onChange={handleChange}
          disabled={disabled}
          rows={2}
        />
      </Grid>
      {!hideActionButton && (
        <ButtonGroup size={12}>
          <Button
            variant="contained"
            color="secondary"
            onClick={savePrsHandle}
            disabled={isSaveDisabled}
            loading={loading}
            sx={{ width: '100%' }}
          >
            Save
          </Button>
        </ButtonGroup>
      )}
    </Grid>
  );
});

export default PrsForm;
